import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentUser } from "./store/slices/authSlice";
import type { RootState, AppDispatch } from "./store";
import CookieConsentBanner from "./components/legal/CookieConsentBanner";

// Create redirect component
const CreateRedirectPage: React.FC = () => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      // If user is authenticated and already has a creator profile, go to dashboard
      if (user.creatorProfile) {
        navigate('/dashboard', { replace: true });
      } else {
        // If authenticated but no creator profile, go to creator onboarding
        navigate('/onboarding/creator', { replace: true });
      }
    } else {
      // If not authenticated, go to register page
      navigate('/register', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Show loading state while redirecting
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-patreon-orange mx-auto" style={{ borderColor: '#FF424D', borderBottomColor: 'transparent' }}></div>
        <p className="mt-4 text-gray-600">Redirecting...</p>
      </div>
    </div>
  );
};

// Layout Components
import DashboardLayout from "./components/layout/DashboardLayout";
import AdminLayout from "./components/layout/AdminLayout";
import ModeratorLayout from "./components/layout/ModeratorLayout";
import SupportLayout from "./components/layout/SupportLayout";

// Page Components
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import CreatorDashboard from "./pages/dashboard/creator/CreatorDashboard";
// Patron Pages (existing)
import PatronDashboard from "./pages/dashboard/patron/PatronDashboard";
import ReferralDashboardPage from "./pages/patron/ReferralDashboardPage";
import AdvancedSettingsPage from "./pages/patron/AdvancedSettingsPage";
import UserSupportPage from "./pages/patron/UserSupportPage";
import PaymentsPage from "./pages/dashboard/patron/PaymentsPage";
import PaymentDetailsPage from "./pages/dashboard/patron/PaymentDetailsPage";
import PaymentMethodsPage from "./pages/dashboard/patron/PaymentMethodsPage";
import TiersPage from "./pages/dashboard/creator/TiersPage";
import PostsPage from "./pages/dashboard/creator/PostsPage";
import AnalyticsPage from "./pages/dashboard/creator/AnalyticsPage";
import PatronInsightsPage from "./pages/dashboard/creator/PatronInsightsPage";
import AnalyticsReportsPage from "./pages/dashboard/creator/AnalyticsReportsPage";
import PatronsPage from "./pages/dashboard/creator/PatronsPage";
import PayoutsPageCreator from "./pages/dashboard/creator/PayoutsPage";
import StripeConnectComplete from "./pages/dashboard/creator/StripeConnectComplete";
import StripeConnectRefresh from "./pages/dashboard/creator/StripeConnectRefresh";
import CheckoutSuccessPage from "./pages/checkout/CheckoutSuccessPage";
import GiftSuccessPage from "./pages/gifts/GiftSuccessPage";
// New Gift & Commission Components
import { GiftPurchaseFlow, GiftRedeemPage, GiftHistoryPage } from "./components/gifts";
import { 
  CommissionRequestForm, 
  CommissionListPage, 
  CommissionDetailPage 
} from "./components/commissions";
import GlobalAccessibility from "./components/accessibility/GlobalAccessibility";
import AccessibilitySettingsPage from "./pages/dashboard/settings/AccessibilitySettingsPage";
import { ABTestDashboard, ABTestCreator, ABTestAnalytics } from "./components/ab-testing";
import { ReferralDashboard, ReferralLeaderboard } from "./components/referrals";
import { ReputationDashboard, ReputationLeaderboard } from "./components/reputation";
import { ForumListPage, ForumTopicPage, CategoryTopicsPage } from "./components/forums";
import PrivacyPolicyPage from "./pages/legal/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/legal/TermsOfServicePage";
import CookiePolicyPage from "./pages/legal/CookiePolicyPage";
import AcceptableUsePolicyPage from "./pages/legal/AcceptableUsePolicyPage";
import LivestreamsPage from "./pages/dashboard/creator/LivestreamsPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import AutomationsPage from "./pages/AutomationsPage";
import AutomationBuilder from "./pages/AutomationBuilder";
// Integration Pages
import DashboardIntegrationsPage from "./pages/dashboard/IntegrationsPage";
import DiscordIntegrationPage from "./pages/dashboard/integrations/DiscordIntegrationPage";
import YouTubeIntegrationPage from "./pages/dashboard/integrations/YouTubeIntegrationPage";
import TwitchIntegrationPage from "./pages/dashboard/integrations/TwitchIntegrationPage";
import WebhookIntegrationPage from "./pages/dashboard/integrations/WebhookIntegrationPage";
import ForecastingDashboard from "./components/ForecastingDashboard";
import TeamMembersPage from "./pages/dashboard/creator/TeamMembersPage";
import PhysicalBenefitsPage from "./pages/dashboard/creator/PhysicalBenefitsPage";
import CommentModerationPage from "./pages/dashboard/creator/CommentModerationPage";
import KYCPageCreator from "./pages/dashboard/creator/KYCPage";
import StatementsPageCreator from "./pages/dashboard/creator/StatementsPage";
import TagManagerPage from "./pages/dashboard/creator/TagManagerPage";
import EmailCapturePage from "./pages/dashboard/creator/EmailCapturePage";
import UTMTrackingPage from "./pages/dashboard/creator/UTMTrackingPage";
import DiscoverPage from "./pages/dashboard/patron/DiscoverPage";
import CreatorPage from "./pages/public/CreatorPage";
import TipsDashboardPage from "./pages/dashboard/creator/TipsDashboardPage";
import TipHistoryPage from "./pages/dashboard/patron/TipHistoryPage";
import CommissionsDashboardPage from "./pages/dashboard/creator/CommissionsDashboardPage";
// Legacy commission pages retained for old routes
import CommissionDetailPageOld from "./pages/dashboard/creator/CommissionDetailPage";
import CommissionRequestPage from "./pages/commissions/CommissionRequestPage";
import PatronCommissionsPage from "./pages/commissions/PatronCommissionsPage";
import PatronCommissionDetailPage from "./pages/commissions/PatronCommissionDetailPage";
import FeaturesPage from "./pages/FeaturesPage";
import PricingPage from "./pages/PricingPage";
import ResourcesPage from "./pages/ResourcesPage";
import PublicDiscoverPage from "./pages/public/PublicDiscoverPage";
import Onboarding from "./pages/onboarding/Onboarding";
import CreatorOnboardingWizard from "./pages/onboarding/CreatorOnboardingWizard";
import SettingsSessions from "./pages/dashboard/SettingsSessions";
import NotificationsPage from "./pages/dashboard/NotificationsPage";
import SearchPage from "./pages/SearchPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminCreatorsPage from "./pages/admin/AdminCreatorsPage";
import AdminReportsPage from "./pages/admin/AdminReportsPage";
import AdminCouponsPage from "./pages/admin/AdminCouponsPage";
import AdminKYCPage from "./pages/admin/AdminKYCPage";
import AdminPayoutsPage from "./pages/admin/AdminPayoutsPage";
import AdminPostsPage from "./pages/admin/AdminPostsPage";
import AdminPaymentsPage from "./pages/admin/AdminPaymentsPage";
import AdminAnalyticsPage from "./pages/admin/AdminAnalyticsPage";
import EngagementAnalyticsPage from "./pages/EngagementAnalyticsPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AdminBrandingPanel from "./pages/admin/AdminBrandingPanel";
import AdminSystemPage from "./pages/admin/AdminSystemPage";
import AdminSubscriptionsPage from "./pages/admin/AdminSubscriptionsPage";
import AdminTiersPage from "./pages/admin/AdminTiersPage";
import DMCampaignsPage from "./pages/dashboard/creator/DMCampaignsPage";
import AutoRespondersPage from "./pages/dashboard/creator/AutoRespondersPage";
import PostTemplatesPage from "./pages/dashboard/creator/PostTemplatesPage";
import TemplateLibraryPage from "./pages/dashboard/creator/TemplateLibraryPage";
import TemplateEditorPage from "./pages/dashboard/creator/TemplateEditorPage";
import DemographicsViewerPage from "./pages/dashboard/creator/DemographicsViewerPage";
import DemographicsPage from "./pages/dashboard/creator/DemographicsPage";
import ABTestingPage from "./pages/dashboard/creator/ABTestingPage";
import EmailCampaignsPage from "./pages/dashboard/creator/EmailCampaignsPage";
import EmailComposerPage from "./pages/dashboard/creator/EmailComposerPage";
import ContentCalendarPage from "./pages/dashboard/creator/ContentCalendarPage";
import SeriesManagerPage from "./pages/dashboard/creator/SeriesManagerPage";
import SeriesEditorPage from "./pages/dashboard/creator/SeriesEditorPage";
import SeriesViewerPage from "./pages/public/SeriesViewerPage";
import EmailAnalyticsPage from "./pages/dashboard/creator/EmailAnalyticsPage";
import EmailTemplatesPage from "./pages/dashboard/creator/EmailTemplatesPage";
import ApiKeysPage from "./pages/dashboard/creator/ApiKeysPage";
import AdminCommentsPage from "./pages/admin/AdminCommentsPage";
import AdminWebhooksPage from "./pages/admin/AdminWebhooksPage";
import AdminAuditLogsPage from "./pages/admin/AdminAuditLogsPage";
import AdminMessagesPage from "./pages/admin/AdminMessagesPage";
import AdminNotificationsPage from "./pages/admin/AdminNotificationsPage";
import AdminEmailTemplatesPage from "./pages/admin/AdminEmailTemplatesPage";
import AdminAPIKeysPage from "./pages/admin/AdminAPIKeysPage";
import AdminSecurityPage from "./pages/admin/AdminSecurityPage";
import AdminFeaturesPage from "./pages/admin/AdminFeaturesPage";
import AdminTagsPage from "./pages/admin/AdminTagsPage";
import AdminBlockedContentPage from "./pages/admin/AdminBlockedContentPage";
import AdminTaxesPage from "./pages/admin/AdminTaxesPage";
import MassActionsPage from "./pages/admin/MassActionsPage";
import AdminDMCAPage from "./pages/admin/AdminDMCAPage";
import AdminContentFiltersPage from "./pages/admin/AdminContentFiltersPage";
import AdminLegalHoldsPage from "./pages/admin/AdminLegalHoldsPage";
import AdminEscalationsPage from "./pages/admin/AdminEscalationsPage";
import AdminAnnouncementsPage from "./pages/admin/AdminAnnouncementsPage";
import AdminImpersonationLogsPage from "./pages/admin/AdminImpersonationLogsPage";
import AdminTierInvitationsPage from "./pages/admin/AdminTierInvitationsPage";
import AdminNewsletterManagerPage from "./pages/admin/AdminNewsletterManagerPage";
import AdminReferralProgramsPage from "./pages/admin/AdminReferralProgramsPage";
import AdminCohortAnalyticsPage from "./pages/admin/AdminCohortAnalyticsPage";
import AdminGDPRRequestsPage from "./pages/admin/AdminGDPRRequestsPage";
import ComplianceReviewPage from "./pages/admin/ComplianceReviewPage";
// Legacy page retained in codebase but not used here
// import CreatorPatronCohortsPage from "./pages/creator/CreatorPatronCohortsPage";
import CohortAnalyticsDashboard from "./components/CohortAnalyticsDashboard";
import GeographicAnalyticsDashboard from "./pages/GeographicAnalyticsDashboard";
import CreatorAdvancedAnalyticsPage from "./pages/creator/CreatorAdvancedAnalyticsPage";
import CreatorEmailCampaignsPage from "./pages/creator/CreatorEmailCampaignsPage";
import BlogPage from "./pages/public/BlogPage";
import HelpCenterPage from "./pages/public/HelpCenterPage";
import SuccessStoriesPage from "./pages/public/SuccessStoriesPage";
import AdvancedSearchPage from "./pages/public/AdvancedSearchPage";
import CategoryLandingPage from "./pages/public/CategoryLandingPage";
import MessagesPage from "./pages/dashboard/MessagesPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
// Settings Module Pages
import ProfileSettingsPage from "./pages/dashboard/settings/ProfileSettingsPage";
import NotificationPreferencesPage from "./pages/dashboard/settings/NotificationPreferencesPage";
import PrivacySettingsPage from "./pages/dashboard/settings/PrivacySettingsPage";
import SecuritySettingsPage from "./pages/dashboard/settings/SecuritySettingsPage";
import SettingsPaymentMethodsPage from "./pages/dashboard/settings/PaymentMethodsPage";
import SubscriptionsPage from "./pages/dashboard/settings/SubscriptionsPage";
import LocalizationSettingsPage from "./pages/dashboard/settings/LocalizationSettingsPage";
import AppearanceSettingsPage from "./pages/dashboard/settings/AppearanceSettingsPage";
import EmailPreferencesPage from "./pages/dashboard/settings/EmailPreferencesPage";
import ConnectedAppsPage from "./pages/dashboard/settings/ConnectedAppsPage";
import DeveloperSettingsPage from "./pages/dashboard/settings/DeveloperSettingsPage";
import BillingHistoryPage from "./pages/dashboard/settings/BillingHistoryPage";
import TaxInformationPage from "./pages/dashboard/settings/TaxInformationPage";
import PayoutSettingsPage from "./pages/dashboard/settings/PayoutSettingsPage";
import ContentFiltersPage from "./pages/dashboard/settings/ContentFiltersPage";
import ExportDataPage from "./pages/dashboard/settings/ExportDataPage";
import DeleteAccountPage from "./pages/dashboard/settings/DeleteAccountPage";
import TwoFactorSetupPage from "./pages/dashboard/settings/TwoFactorSetupPage";
import LoginHistoryPage from "./pages/dashboard/settings/LoginHistoryPage";
import BackupCodesPage from "./pages/dashboard/settings/BackupCodesPage";
import TrustedDevicesPage from "./pages/dashboard/settings/TrustedDevicesPage";
import AddPaymentMethodPage from "./pages/dashboard/settings/AddPaymentMethodPage";
import GroupChatPage from "./pages/dashboard/GroupChatPage";
import ModeratorDashboard from "./pages/moderator/ModeratorDashboard";
import SupportDashboard from "./pages/support/SupportDashboard";
import SupportTicketDetail from "./pages/support/SupportTicketDetail";
// Discord Integration Pages
import DiscordConnectPage from './pages/dashboard/creator/DiscordConnectPage';
import DiscordCallbackPage from './pages/dashboard/creator/DiscordCallbackPage';
import DiscordRoleMappingPage from './pages/dashboard/creator/DiscordRoleMappingPage';
import ToastContainer from "./components/ui/Toast";

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "creator" | "patron" | "admin" | "moderator" | "support";
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
}) => {
  const { isAuthenticated, user, isLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role requirements
  if (requiredRole) {
    if (requiredRole === 'admin' && !user?.isAdmin) {
      return <Navigate to="/" replace />;
    }
    if (requiredRole === 'moderator' && !(user?.isModerator || user?.isAdmin)) {
      return <Navigate to="/" replace />;
    }
    if (requiredRole === 'support' && !(user?.isSupport || user?.isAdmin)) {
      return <Navigate to="/" replace />;
    }
    if ((requiredRole === 'creator' || requiredRole === 'patron')) {
      const isCreator = !!user?.creatorProfile;
      if ((requiredRole === 'creator' && !isCreator) || (requiredRole === 'patron' && isCreator)) {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  return <>{children}</>;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useSelector(
    (state: RootState) => state.auth,
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Dashboard wrapper component that determines user type
const DashboardWrapper: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userType = user?.creatorProfile ? "creator" : "patron";

  return (
    <DashboardLayout userType={userType}>
      {children || <Outlet />}
    </DashboardLayout>
  );
};

// Messages wrapper component that determines user type
const MessagesWrapper: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userType = user?.creatorProfile ? "creator" : "patron";

  return (
    <DashboardLayout userType={userType}>
      <MessagesPage />
    </DashboardLayout>
  );
};

// Settings wrapper component
const SettingsWrapper: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userType = user?.creatorProfile ? "creator" : "patron";

  return (
    <DashboardLayout userType={userType}>
      <SettingsPage />
    </DashboardLayout>
  );
};

// Settings Sessions wrapper component
const SettingsSessionsWrapper: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const userType = user?.creatorProfile ? "creator" : "patron";

  return (
    <DashboardLayout userType={userType}>
      <SettingsSessions />
    </DashboardLayout>
  );
};

// Admin wrapper component
const AdminWrapper: React.FC = () => {
  return (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  );
};

// Dashboard home that chooses between Creator or Patron dashboard
const DashboardHome: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  
  React.useEffect(() => {
    if (user?.isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);
  
  if (user?.isAdmin) {
    return null; // Will redirect
  }
  
  const isCreator = !!user?.creatorProfile;
  return isCreator ? <CreatorDashboard /> : <PatronDashboard />;
};

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, token } = useSelector(
    (state: RootState) => state.auth,
  );

  // Check for existing user session on app load
  useEffect(() => {
    if (token && !isAuthenticated) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, token, isAuthenticated]);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <div className="App">
        <GlobalAccessibility />
        <ToastContainer />
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <HomePage />
              </PublicRoute>
            }
          />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/discover" element={<PublicDiscoverPage />} />
          <Route path="/series/:seriesId" element={<SeriesViewerPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/success-stories" element={<SuccessStoriesPage />} />
          <Route path="/advanced-search" element={<AdvancedSearchPage />} />
          <Route path="/category/:category" element={<CategoryLandingPage />} />
          
          {/* Checkout Routes */}
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/gift/redeem" element={<GiftRedeemPage />} />
          <Route path="/gift/success" element={<GiftSuccessPage />} />
          
          {/* Gift Purchase Route - Public (requires auth inside component) */}
          <Route 
            path="/gift/purchase/:creatorId" 
            element={
              <ProtectedRoute>
                <GiftPurchaseFlow />
              </ProtectedRoute>
            } 
          />
          
          {/* Legal Routes */}
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/legal/terms" element={<TermsOfServicePage />} />
          <Route path="/legal/cookies" element={<CookiePolicyPage />} />
          <Route path="/legal/acceptable-use" element={<AcceptableUsePolicyPage />} />
          
          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminWrapper />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="creators" element={<AdminCreatorsPage />} />
            <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="tiers" element={<AdminTiersPage />} />
            <Route path="posts" element={<AdminPostsPage />} />
            <Route path="comments" element={<AdminCommentsPage />} />
              <Route path="messages" element={<AdminMessagesPage />} />
            <Route path="reports" element={<AdminReportsPage />} />
            <Route path="payments" element={<AdminPaymentsPage />} />
            <Route path="coupons" element={<AdminCouponsPage />} />
            <Route path="kyc" element={<AdminKYCPage />} />
            <Route path="payouts" element={<AdminPayoutsPage />} />
              <Route path="taxes" element={<AdminTaxesPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
              <Route path="notifications" element={<AdminNotificationsPage />} />
              <Route path="email-templates" element={<AdminEmailTemplatesPage />} />
            <Route path="webhooks" element={<AdminWebhooksPage />} />
              <Route path="api-keys" element={<AdminAPIKeysPage />} />
            <Route path="audit-logs" element={<AdminAuditLogsPage />} />
              <Route path="security" element={<AdminSecurityPage />} />
              <Route path="features" element={<AdminFeaturesPage />} />
            <Route path="tags" element={<AdminTagsPage />} />
            <Route path="blocked-content" element={<AdminBlockedContentPage />} />
            <Route path="mass-actions" element={<MassActionsPage />} />
            <Route path="dmca" element={<AdminDMCAPage />} />
            <Route path="content-filters" element={<AdminContentFiltersPage />} />
            <Route path="legal-holds" element={<AdminLegalHoldsPage />} />
            <Route path="gdpr-requests" element={<AdminGDPRRequestsPage />} />
            <Route path="compliance-review" element={<ComplianceReviewPage />} />
            <Route path="escalations" element={<AdminEscalationsPage />} />
            <Route path="announcements" element={<AdminAnnouncementsPage />} />
            <Route path="impersonation-logs" element={<AdminImpersonationLogsPage />} />
            <Route path="tier-invitations" element={<AdminTierInvitationsPage />} />
            <Route path="newsletters" element={<AdminNewsletterManagerPage />} />
            <Route path="referral-programs" element={<AdminReferralProgramsPage />} />
            <Route path="cohort-analytics" element={<AdminCohortAnalyticsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="branding" element={<AdminBrandingPanel />} />
            <Route path="system" element={<AdminSystemPage />} />
          </Route>

          {/* Moderator Routes */}
          <Route
            path="/moderator"
            element={
              <ProtectedRoute requiredRole="moderator">
                <ModeratorLayout>
                  <Outlet />
                </ModeratorLayout>
              </ProtectedRoute>
            }
          >
            <Route index element={<ModeratorDashboard />} />
            <Route path="reports" element={<ModeratorDashboard />} />
            <Route path="actions" element={<ModeratorDashboard />} />
            <Route path="warnings" element={<ModeratorDashboard />} />
          </Route>

          {/* Support Routes */}
          <Route
            path="/support"
            element={
              <ProtectedRoute requiredRole="support">
                <SupportLayout>
                  <Outlet />
                </SupportLayout>
              </ProtectedRoute>
            }
          >
            <Route index element={<SupportDashboard />} />
            <Route path="tickets" element={<SupportDashboard />} />
            <Route path="tickets/:ticketId" element={<SupportTicketDetail />} />
          </Route>

          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/onboarding/creator" element={<CreatorOnboardingWizard />} />
          {/* Create route - redirects to creator onboarding if authenticated, or register if not */}
          <Route
            path="/create"
            element={<CreateRedirectPage />}
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

          {/* Protected Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardWrapper />
              </ProtectedRoute>
            }
          >
            {/* Default dashboard landing based on user type */}
            <Route index element={<DashboardHome />} />
          </Route>

          {/* Creator-only Routes */}
          <Route
            path="/dashboard/patrons"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <PatronsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/tips"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <TipsDashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/commissions"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <CommissionsDashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/commissions/:id"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <CommissionDetailPageOld />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/email-campaigns"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <EmailCampaignsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/email-campaigns/:id/edit"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <EmailComposerPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/email-campaigns/new"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <EmailComposerPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/content-calendar"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <ContentCalendarPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/integrations"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <IntegrationsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/integrations/discord/connect"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <DiscordConnectPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/integrations/discord/callback"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <DiscordCallbackPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/integrations/discord/manage"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <DiscordRoleMappingPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/automations"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <AutomationsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/automations/new"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <AutomationBuilder />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/automations/:id/edit"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <AutomationBuilder />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/series"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <SeriesManagerPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/series/:seriesId/edit"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <SeriesEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/email-campaigns/:id/analytics"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <EmailAnalyticsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/email-campaigns/templates"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <EmailTemplatesPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/posts"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <PostsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/tiers"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <TiersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/analytics"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <AnalyticsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reports"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <AnalyticsReportsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/patron-insights"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <PatronInsightsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/api-keys"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <ApiKeysPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/cohorts"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  {/* Legacy cohort list view retained below; replace with CohortAnalyticsDashboard for advanced retention analytics */}
                  <CohortAnalyticsDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/geographic"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <GeographicAnalyticsDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/engagement"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <EngagementAnalyticsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/analytics-advanced"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <CreatorAdvancedAnalyticsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/forecasting"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <ForecastingDashboard />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/email-campaigns"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <CreatorEmailCampaignsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/payouts"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <PayoutsPageCreator />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/payouts/connect/complete"
            element={
              <ProtectedRoute requiredRole="creator">
                <StripeConnectComplete />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/payouts/connect/refresh"
            element={
              <ProtectedRoute requiredRole="creator">
                <StripeConnectRefresh />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/kyc"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <KYCPageCreator />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/statements"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <StatementsPageCreator />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/livestreams"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <LivestreamsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/team"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <TeamMembersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/physical-benefits"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <PhysicalBenefitsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/moderation"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <CommentModerationPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/tags"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <TagManagerPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/email-capture"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <EmailCapturePage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/utm-tracking"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <UTMTrackingPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/dm-campaigns"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <DMCampaignsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/auto-responders"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <AutoRespondersPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/post-templates"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <PostTemplatesPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/demographics"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <DemographicsViewerPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/ab-testing"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <ABTestingPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/demographics"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <DemographicsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Template Routes */}
          <Route
            path="/dashboard/templates"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <TemplateLibraryPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/templates/new"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <TemplateEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/templates/:id/edit"
            element={
              <ProtectedRoute requiredRole="creator">
                <DashboardLayout userType="creator">
                  <TemplateEditorPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Patron Routes */}
          <Route
            path="/dashboard/memberships"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <div className="p-6">
                    <h1 className="text-2xl font-bold">My Memberships</h1>
                    <p className="text-gray-600">
                      Manage your creator memberships.
                    </p>
                  </div>
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/tips/history"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <TipHistoryPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/commissions/new"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <CommissionRequestPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/commissions/requests"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <PatronCommissionsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/commissions/patron/:id"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <PatronCommissionDetailPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/discover"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <DiscoverPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/payments"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <PaymentsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/gifts"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <GiftHistoryPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route path="/gifts/history" element={<Navigate to="/dashboard/gifts" replace />} />
          <Route path="/gifts" element={<Navigate to="/dashboard/gifts" replace />} />
          
          {/* Unified Commission Routes - Work for both creators and patrons */}
          <Route
            path="/commissions"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <CommissionListPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/commissions/new"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <CommissionRequestForm />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/commissions/:id"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <CommissionDetailPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/payments/:id"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <PaymentDetailsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/payment-methods"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <PaymentMethodsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/referrals"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <ReferralDashboardPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/advanced"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <AdvancedSettingsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/messages"
            element={
              <ProtectedRoute>
                <MessagesWrapper />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/group-chat/:chatId?"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <GroupChatPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/activity"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <NotificationsPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/support"
            element={
              <ProtectedRoute requiredRole="patron">
                <DashboardLayout userType="patron">
                  <UserSupportPage />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />

          {/* Settings Route (accessible to both) */}
          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute>
                <SettingsWrapper />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/sessions"
            element={
              <ProtectedRoute>
                <SettingsSessionsWrapper />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/accessibility"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <AccessibilitySettingsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route path="/settings/accessibility" element={<Navigate to="/dashboard/settings/accessibility" replace />} />

          {/* Settings Module Routes */}
          <Route
            path="/dashboard/settings/profile"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ProfileSettingsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/notifications"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <NotificationPreferencesPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/privacy"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <PrivacySettingsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/security"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <SecuritySettingsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/payment-methods"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <SettingsPaymentMethodsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/subscriptions"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <SubscriptionsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/localization"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <LocalizationSettingsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/appearance"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <AppearanceSettingsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/email-preferences"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <EmailPreferencesPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/connected-apps"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ConnectedAppsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/developer"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <DeveloperSettingsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/billing-history"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <BillingHistoryPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/tax-info"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <TaxInformationPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/payout"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <PayoutSettingsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/content-filters"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ContentFiltersPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/export-data"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ExportDataPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/delete-account"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <DeleteAccountPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/two-factor/setup"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <TwoFactorSetupPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/login-history"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <LoginHistoryPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/backup-codes"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <BackupCodesPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/trusted-devices"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <TrustedDevicesPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings/add-payment-method"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <AddPaymentMethodPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />

          {/* AB Testing Routes */}
          <Route
            path="/dashboard/ab-testing"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ABTestDashboard />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/ab-testing/create"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ABTestCreator />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/ab-testing/:testId"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ABTestAnalytics />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />

          {/* Referral Program Routes */}
          <Route
            path="/dashboard/referrals"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ReferralDashboard />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/referrals/leaderboard"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ReferralLeaderboard />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />

          {/* Reputation & Badges Routes */}
          <Route
            path="/dashboard/reputation"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ReputationDashboard />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reputation/leaderboard"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <ReputationLeaderboard />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />

          {/* Forums Routes */}
          <Route path="/forums" element={<ForumListPage />} />
          <Route
            path="/forums/:forumSlug/categories/:categoryId"
            element={<CategoryTopicsPage />}
          />
          <Route
            path="/forums/:forumSlug/topics/:topicId"
            element={<ForumTopicPage />}
          />

          {/* Integrations Routes */}
          <Route
            path="/dashboard/integrations"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <DashboardIntegrationsPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/integrations/discord"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <DiscordIntegrationPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/integrations/youtube"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <YouTubeIntegrationPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/integrations/twitch"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <TwitchIntegrationPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/integrations/webhooks"
            element={
              <ProtectedRoute>
                <DashboardWrapper>
                  <WebhookIntegrationPage />
                </DashboardWrapper>
              </ProtectedRoute>
            }
          />

          {/* Public Creator Page */}
          <Route path="/creator/:username" element={<CreatorPage />} />

          {/* Catch-all route */}
          <Route
            path="*"
            element={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                  <p className="text-gray-600 mb-8">Page not found</p>
                  <button
                    onClick={() => window.history.back()}
                    className="bg-gradient-to-r from-orange-500 to-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg transition-shadow"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            }
          />
        </Routes>
        <CookieConsentBanner />
      </div>
    </Router>
  );
};

export default App;
